import React from 'react';
import Panel from '../../components/ui/Panel';
import ComponentShowcase from '../../components/ui/ComponentShowcase';
import InventorySlot from '../../components/ui/InventorySlot';

const InventorySlotPage: React.FC = () => {
  const basicSlotCode = `<InventorySlot />`;

  const withItemCode = `<InventorySlot 
  item={{
    name: "Rune Scimitar",
    image: "/images/items/rune_scimitar.png" // Path to your item image
  }}
/>`;

  const withQuantityCode = `<InventorySlot 
  item={{
    name: "Coins",
    image: "/images/items/coins.png", // Path to your item image
    quantity: 100000
  }}
/>`;

  const interactiveCode = `<InventorySlot 
  item={{
    name: "Dragon Dagger",
    image: "/images/items/dragon_dagger.png" // Path to your item image
  }}
  onClick={() => alert('Dragon Dagger clicked!')}
/>`;

  return (
    <div>
      <h1 id="overview" className="text-3xl text-osrs-gold font-bold uppercase mb-4">Inventory Slot</h1>
      <p className="text-osrs-yellow mb-8">
        Inventory slots are the foundational building blocks for item displays in the OSRS interface.
        They represent the visual containers that hold items in the player's inventory, bank, equipment, and other item storage interfaces.
      </p>

      <section id="examples" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Examples</h2>
        
        <ComponentShowcase
          id="basic"
          title="Empty Slot"
          description="A basic empty inventory slot."
          component={
            <div className="flex justify-center p-6">
              <InventorySlot />
            </div>
          }
          code={basicSlotCode}
          className="mb-10"
        />
        
        <ComponentShowcase
          id="with-item"
          title="Slot with Item"
          description="An inventory slot containing an item."
          component={
            <div className="flex justify-center p-6">
              <InventorySlot 
                item={{
                  name: "Rune Scimitar",
                  image: undefined
                }}
              />
            </div>
          }
          code={withItemCode}
          className="mb-10"
        />
        
        <ComponentShowcase
          id="with-quantity"
          title="Slot with Quantity"
          description="An inventory slot containing a stackable item with quantity."
          component={
            <div className="flex justify-center p-6">
              <InventorySlot 
                item={{
                  name: "Coins",
                  image: undefined,
                  quantity: 100000
                }}
              />
            </div>
          }
          code={withQuantityCode}
          className="mb-10"
        />
        
        <ComponentShowcase
          id="interactive"
          title="Interactive Slot"
          description="An inventory slot that responds to user interaction."
          component={
            <div className="flex justify-center p-6">
              <InventorySlot 
                item={{
                  name: "Dragon Dagger",
                  image: undefined
                }}
                onClick={() => alert('Dragon Dagger clicked!')}
              />
            </div>
          }
          code={interactiveCode}
          className="mb-10"
        />
      </section>
      
      <section id="states" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Slot States</h2>
        
        <Panel className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-xl text-osrs-gold font-bold mb-3">Empty State</h3>
              <div className="flex justify-center">
                <InventorySlot />
              </div>
              <p className="text-osrs-yellow mt-3 text-center">
                Empty slot with default styling
              </p>
            </div>
            
            <div>
              <h3 className="text-xl text-osrs-gold font-bold mb-3">Selected State</h3>
              <div className="flex justify-center">
                <InventorySlot selected item={{ name: 'Bones', quantity: 3 }} />
              </div>
              <p className="text-osrs-yellow mt-3 text-center">
                Selected slot with a white outline
              </p>
            </div>
            
            <div>
              <h3 className="text-xl text-osrs-gold font-bold mb-3">Disabled State</h3>
              <div className="flex justify-center">
                <InventorySlot disabled />
              </div>
              <p className="text-osrs-yellow mt-3 text-center">
                Disabled/locked slot
              </p>
            </div>
          </div>
        </Panel>
      </section>
      
      <section id="usage-guidelines" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Usage Guidelines</h2>
        
        <Panel className="mb-6">
          <h3 id="when-to-use" className="text-xl text-osrs-gold font-bold mb-3">When to Use</h3>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use inventory slots to create item grids such as inventory, bank, or equipment interfaces</li>
            <li>Use for item selection interfaces like shops or trade windows</li>
            <li>Use for displaying collectibles, achievements, or unlockable items</li>
            <li>Add quantity indicators for stackable items</li>
            <li>Implement interactive functionality for actions like equipping, dropping, or examining items</li>
          </ul>
        </Panel>
        
        <Panel className="mb-6">
          <h3 id="best-practices" className="text-xl text-osrs-gold font-bold mb-3">Best Practices</h3>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Maintain consistent 32x32 pixel item images for authentic OSRS look</li>
            <li>Ensure item images have transparent backgrounds</li>
            <li>For stackable items, position quantity text in the bottom-right corner</li>
            <li>Format large quantities with K/M abbreviations (e.g., 100K for 100,000)</li>
            <li>Add hover effects to indicate interactivity</li>
            <li>Consider implementing tooltips or right-click context menus for item actions</li>
            <li>For grid layouts, maintain the standard 4x7 or 8x3 OSRS inventory patterns</li>
          </ul>
        </Panel>
        
        <Panel className="mb-6">
          <h3 id="accessibility" className="text-xl text-osrs-gold font-bold mb-3">Accessibility</h3>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Include alt text for item images that describes the item</li>
            <li>Ensure interactive slots can be accessed via keyboard navigation</li>
            <li>Add appropriate ARIA attributes for interactive functionality</li>
            <li>Consider implementing keyboard shortcuts for common inventory actions</li>
            <li>Ensure sufficient contrast for text on inventory slots</li>
          </ul>
        </Panel>
      </section>
      
      <section id="api-reference" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">API Reference</h2>
        
        <Panel>
          <table className="w-full text-osrs-yellow">
            <thead>
              <tr className="border-b border-osrs-border">
                <th className="text-left py-2 px-4 text-osrs-gold">Prop</th>
                <th className="text-left py-2 px-4 text-osrs-gold">Type</th>
                <th className="text-left py-2 px-4 text-osrs-gold">Default</th>
                <th className="text-left py-2 px-4 text-osrs-gold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-osrs-border">
                <td className="py-2 px-4">item</td>
                <td className="py-2 px-4">
                  <code>{'{ name: string, image?: string, quantity?: number }'}</code>
                </td>
                <td className="py-2 px-4">
                  <code>undefined</code>
                </td>
                <td className="py-2 px-4">The item to display in the slot</td>
              </tr>
              <tr className="border-b border-osrs-border">
                <td className="py-2 px-4">onClick</td>
                <td className="py-2 px-4">
                  <code>{'() => void'}</code>
                </td>
                <td className="py-2 px-4">
                  <code>undefined</code>
                </td>
                <td className="py-2 px-4">Function to call when the slot is clicked</td>
              </tr>
              <tr>
                <td className="py-2 px-4">className</td>
                <td className="py-2 px-4">
                  <code>string</code>
                </td>
                <td className="py-2 px-4">
                  <code>''</code>
                </td>
                <td className="py-2 px-4">Additional CSS classes to apply to the slot</td>
              </tr>
            </tbody>
          </table>
        </Panel>
      </section>
    </div>
  );
};

export default InventorySlotPage; 